// Text handoff: one coach at a time, from the sender's OWN phone. The server renders the message and
// returns an sms: link for exactly one recipient; the sender's click on that link opens Messages. We
// never send anything. After the click, window focus (or "I'm back") asks "Sent?" and the answer is
// recorded with /confirm. Coaches with no usable phone or do-not-contact are skipped with a reason.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCrm } from '../../lib/crm';
import { coachName } from '../../pages/CoachesPage';
import TemplatePicker from './TemplatePicker';

const REASONS = { no_phone: 'No usable phone', do_not_contact: 'Do not contact' };

function skipReason(rel) {
  if (rel.status === 'do_not_contact') return REASONS.do_not_contact;
  if (!rel.phone) return REASONS.no_phone;
  return null;
}

export default function TextFlow({ ids, rows, onDone }) {
  const crm = useCrm();
  const [stage, setStage] = useState('loading'); // loading | setup | run | summary
  const [loadError, setLoadError] = useState('');
  const [rels, setRels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [pick, setPick] = useState({ templateId: null, body: '' });
  const [pre, setPre] = useState([]);       // skipped before sending: { rel, reason }
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [current, setCurrent] = useState(null); // { activity_id, body, sms_link } for queue[idx]
  const [opened, setOpened] = useState(false);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(0);
  const [skipped, setSkipped] = useState([]);
  const started = useRef('');
  const stopped = useRef(false); // set by Stop so a late /text reply is confirmed as not sent, not shown
  // Callers pass fresh array literals; key the load on the ids' content so it runs once.
  const idsKey = ids.join(',');
  const input = useRef({ ids, rows });
  input.current = { ids, rows };

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { ids: want, rows: have } = input.current;
        const [tpls, loaded] = await Promise.all([
          crm.listTemplates(),
          Promise.all(want.map((id) => have?.find((r) => r.id === id) || crm.getRelationship(id).catch(() => null))),
        ]);
        if (!live) return;
        setTemplates(tpls);
        setPick({ templateId: tpls[0]?.id ?? null, body: '' });
        setRels(loaded.map((r, i) => r || { id: want[i], failed: true, coach: {} }));
        setStage('setup');
      } catch {
        if (live) setLoadError('Could not load the templates.');
      }
    })();
    return () => { live = false; };
  }, [crm, idsKey]);

  const begin = () => {
    const skip = []; const go = [];
    rels.forEach((r) => {
      const why = r.failed ? 'Could not load' : skipReason(r);
      if (why) skip.push({ rel: r, reason: why }); else go.push(r);
    });
    setPre(skip); setSkipped(skip); setQueue(go); setIdx(0);
    setStage(go.length ? 'run' : 'summary');
  };

  const advance = useCallback(() => {
    setCurrent(null); setOpened(false); setAsking(false); setError('');
    setIdx((i) => i + 1);
  }, []);

  useEffect(() => { if (stage === 'run' && idx >= queue.length) setStage('summary'); }, [stage, idx, queue.length]);

  // Build the message for the current coach (once per coach/attempt, also under StrictMode).
  useEffect(() => {
    if (stage !== 'run' || idx >= queue.length) return;
    const key = `${idx}:${attempt}`;
    if (started.current === key) return;
    started.current = key;
    const rel = queue[idx];
    crm.startText(rel.id, pick.templateId != null ? { templateId: pick.templateId } : { body: pick.body })
      .then((r) => {
        if (stopped.current) { crm.confirmText(r.activity_id, false).catch(() => {}); return; }
        setCurrent(r);
      })
      .catch((err) => {
        if (stopped.current) return;
        const d = err.response?.data?.detail;
        if (err.response?.status === 422 && REASONS[d]) {
          setSkipped((s) => [...s, { rel, reason: REASONS[d] }]);
          advance();
        } else {
          setError(typeof d === 'string' ? d : 'Could not build this message.');
        }
      });
  }, [stage, idx, attempt, queue, pick, crm, advance]);

  // Coming back from Messages: ask "Sent?" (never auto-confirm).
  useEffect(() => {
    if (!opened || asking) return undefined;
    const onFocus = () => setAsking(true);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [opened, asking]);

  const answer = async (wasSent) => {
    if (busy || !current) return;
    setBusy(true); setError('');
    const rel = queue[idx];
    try {
      await crm.confirmText(current.activity_id, wasSent);
    } catch (err) {
      if (err.response?.status !== 409) { setError('Could not record that. Try again.'); setBusy(false); return; }
    }
    if (wasSent) setSent((n) => n + 1);
    else setSkipped((s) => [...s, { rel, reason: 'Not sent' }]);
    setBusy(false);
    advance();
  };

  // Stop: an open started row is confirmed as not sent, then everyone after it is listed as not reached.
  const stop = async () => {
    if (busy) return;
    stopped.current = true;
    setBusy(true); setError('');
    if (current) {
      try {
        await crm.confirmText(current.activity_id, false);
      } catch (err) {
        if (err.response?.status !== 409) { stopped.current = false; setError('Could not record that. Try again.'); setBusy(false); return; }
      }
    }
    setSkipped((s) => [
      ...s,
      ...(current ? [{ rel: queue[idx], reason: 'Not sent' }] : []),
      ...queue.slice(idx + (current ? 1 : 0)).map((r) => ({ rel: r, reason: 'Not reached' })),
    ]);
    setBusy(false);
    setStage('summary');
  };

  if (loadError) return <div role="alert" className="cr-error">{loadError}</div>;
  if (stage === 'loading') return <p className="cr-muted">Loading…</p>;

  if (stage === 'setup') {
    const skipNow = rels.map((r) => ({ rel: r, reason: r.failed ? 'Could not load' : skipReason(r) })).filter((s) => s.reason);
    const ready = rels.length - skipNow.length;
    const canStart = ready > 0 && (pick.templateId != null || pick.body.trim());
    return (
      <div data-testid="textflow_setup">
        <h2 className="cr-modal__title">Text {rels.length === 1 ? coachName(rels[0].coach) || 'coach' : `${rels.length} coaches`}</h2>
        <TemplatePicker templates={templates} templateId={pick.templateId} body={pick.body} onChange={setPick} />
        <p className="cr-muted">{ready} will get a message, one at a time, from your own phone.</p>
        {skipNow.length > 0 && (
          <ul className="cr-list">
            {skipNow.map((s) => (
              <li key={s.rel.id} className="cr-list__row" data-testid={`textflow_skip_${s.rel.id}`}>
                <span>{coachName(s.rel.coach) || `Coach ${s.rel.id}`}</span><span className="cr-sub">Skipped: {s.reason}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="cr-modal__actions">
          <button type="button" className="adm-btn" onClick={onDone} data-testid="textflow_cancel">Cancel</button>
          <button type="button" className="adm-btn adm-btn--primary" disabled={!canStart} onClick={begin} data-testid="textflow_start">Start</button>
        </div>
      </div>
    );
  }

  if (stage === 'summary') {
    return (
      <div data-testid="textflow_summary">
        <h2 className="cr-modal__title">Sent {sent} · Skipped {skipped.length}</h2>
        {skipped.length > 0 && (
          <ul className="cr-list">
            {skipped.map((s, i) => (
              <li key={`${s.rel.id}-${i}`} className="cr-list__row">
                <span>{coachName(s.rel.coach) || `Coach ${s.rel.id}`}</span><span className="cr-sub">{s.reason}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="cr-modal__actions">
          <button type="button" className="adm-btn adm-btn--primary" onClick={onDone} data-testid="textflow_done">Done</button>
        </div>
      </div>
    );
  }

  const rel = queue[idx];
  if (!rel) return null; // idx just passed the end; the effect above switches to the summary
  return (
    <div data-testid="textflow_run">
      <p className="cr-sub">Coach {idx + 1} of {queue.length}{pre.length ? ` (${pre.length} skipped)` : ''}</p>
      <h2 className="cr-modal__title">{coachName(rel.coach)}{rel.coach.school ? `, ${rel.coach.school}` : ''}</h2>
      {current ? (
        <>
          <p className="cr-notes" data-testid="textflow_body">{current.body}</p>
          {asking ? (
            <div className="cr-actions">
              <strong>Sent?</strong>
              <button type="button" className="adm-btn adm-btn--primary" disabled={busy} onClick={() => answer(true)} data-testid="textflow_yes">Yes</button>
              <button type="button" className="adm-btn" disabled={busy} onClick={() => answer(false)} data-testid="textflow_no">No</button>
            </div>
          ) : (
            <div className="cr-actions">
              <a className="adm-btn adm-btn--dark" href={current.sms_link} onClick={() => setOpened(true)} data-testid="textflow_open">Open in Messages</a>
              {opened && <button type="button" className="adm-btn" onClick={() => setAsking(true)} data-testid="textflow_back">I&rsquo;m back</button>}
              {!opened && <button type="button" className="adm-btn" disabled={busy} onClick={() => answer(false)} data-testid="textflow_skip">Skip this coach</button>}
            </div>
          )}
        </>
      ) : !error && <p className="cr-muted">Preparing message…</p>}
      <div className="cr-actions">
        <button type="button" className="adm-btn adm-btn--sm" disabled={busy || asking || opened} onClick={stop} data-testid="textflow_stop">Stop</button>
      </div>
      {error && (
        <div role="alert" className="cr-error">
          {error}{' '}
          {!current && (
            <>
              <button type="button" className="adm-btn adm-btn--sm" onClick={() => { setError(''); setAttempt((a) => a + 1); }} data-testid="textflow_retry">Retry</button>
              <button type="button" className="adm-btn adm-btn--sm" onClick={() => { setSkipped((s) => [...s, { rel, reason: 'Could not build message' }]); advance(); }}>Skip</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
